import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Component_InboxItem_Base } from './Component_InboxItem_Base.js';
import { inboxItemsStatus_CompletedAndViewed, inboxItemStatus_Completed, inboxItemStatus_Pending } from './types.js';
import { RuntimeOrderStatus_Enum } from '@app/advisor-shared';
import { stopPropagation } from '@nu-art/thunderstorm-frontend/index';
import { ModuleFE_Advisor_Action, ModuleFE_Advisor_AdvisorWizard, ModuleFE_EMInterface, ModuleFE_RuntimeOrder } from '@app/advisor-frontend';
import { asArray, exists, ImplementationMissingException, MUSTNeverHappenException } from '@nu-art/ts-common';
import { getOrderResourceType } from '../../../_utils/get-resource-type.js';
import { ModuleFE_AdvisorBoard } from '../../../_enum.js';
import { PanelKey_Spotlight } from '../../../_shared.js';
import { ModuleFE_Spotlight } from '../../../modules/ModuleFE_Spotlight/index.js';
import { ModuleFE_Inbox } from '../../../modules/module-inbox/ModuleFE_Inbox.js';
import { ModuleFE_PanelFindings } from '../../../modules/ModuleFE_PanelFindings/ModuleFE_PanelFindings.js';
import { Advisor_DialogConfirmation } from '../Advisor_DialogConfirmation/Advisor_DialogConfirmation.js';
import { ModuleFE_AdvisorAnalytics } from '../../../_analytics/consts.js';
const completedOrderStatuses = [
    RuntimeOrderStatus_Enum.RESULTED,
    RuntimeOrderStatus_Enum.COMPLETED,
];
export class Component_InboxItem_Order extends Component_InboxItem_Base {
    //######################### Life Cycle #########################
    __onRuntimeOrdersUpdated = (...params) => {
        const rtOrders = asArray(params[1]);
        const updatedRTOrder = rtOrders.find(rtOrder => rtOrder._id === this.state.rtOrder._id);
        if (!updatedRTOrder)
            return;
        //Set new rtOrder data and re-calculate status
        this.reDeriveState({ rtOrder: updatedRTOrder });
    };
    __onInboxItemUpdated(selectedItem) {
        if (selectedItem?.itemId === this.state.rtOrder._id)
            return this.setState({ selected: selectedItem?.itemId === this.state.rtOrder._id });
        // @ts-ignore - this is ugly but set state doesn't behave nicely with boolean toggling
        delete this.state.selected;
        this.forceUpdate();
    }
    deriveStateFromProps(nextProps, state) {
        state.rtOrder = nextProps.rtOrder;
        state.asyncFetch ??= false;
        state.dbOrder = ModuleFE_Advisor_Action.cache.unique(state.rtOrder.orderId);
        if (!ModuleFE_Advisor_Action.is.order(state.dbOrder)) {
            if (!state.dbOrder)
                throw new MUSTNeverHappenException(`Could not get db order for id ${state.rtOrder.orderId}`);
            const orderType = state.dbOrder.type;
            throw new MUSTNeverHappenException(`Got invalid action with type ${orderType}`);
        }
        state = super.deriveStateFromProps(nextProps, state);
        return state;
    }
    //######################### Abstract Implementation #########################
    getIconKey(state) {
        const resourceType = getOrderResourceType(state.dbOrder);
        switch (resourceType) {
            case 'consult':
                return 'advisor_avn_consultation';
            case 'ekg':
                return 'advisor_avn_ekg';
            case 'imaging':
                return 'advisor_avn_imaging';
            case 'treatment':
                return 'advisor_avn_medication';
            default:
                return 'advisor_avn_labs';
        }
    }
    getTimestamp(state) {
        return state.rtOrder.timeResulted
            ?? state.rtOrder.timeCreated
            ?? state.rtOrder.__created;
    }
    getLabel(state) {
        const existingOrdersAmount = this.getOrderCountForVariableId(state.rtOrder);
        return `${state.dbOrder.name}${existingOrdersAmount > 0 ? ` #${existingOrdersAmount + 1}` : ''}`;
    }
    getStatus(state) {
        switch (state.rtOrder.orderStatus) {
            case RuntimeOrderStatus_Enum.RECOMMENDED:
            case RuntimeOrderStatus_Enum.PENDING:
            case RuntimeOrderStatus_Enum.ACCEPTED:
            case RuntimeOrderStatus_Enum.PENDING_SIGNATURE:
            case RuntimeOrderStatus_Enum.FOUND_IN_FHIR:
                return inboxItemStatus_Pending;
            case RuntimeOrderStatus_Enum.RESULTED:
                return inboxItemStatus_Completed;
            case RuntimeOrderStatus_Enum.COMPLETED:
                return inboxItemsStatus_CompletedAndViewed;
            default:
                throw new ImplementationMissingException(`Missing implementation for order status ${state.rtOrder.orderStatus}`);
        }
    }
    async onItemClick(e) {
        stopPropagation(e);
        //Report analytics event
        const dbOrder = ModuleFE_Advisor_Action.cache.unique(this.state.rtOrder.orderId);
        if (exists(dbOrder))
            ModuleFE_AdvisorAnalytics.trigger.inbox.inbox_orderClick({
                dbOrderId: dbOrder._id,
                dbOrderLabel: dbOrder.name,
                orderStatus: this.state.rtOrder.orderStatus,
            });
        //handle resolve order
        if (!completedOrderStatuses.includes(this.state.rtOrder.orderStatus))
            return await this.resolveOrder();
        // close spotlight panel if jumping to finding
        const panelItem = ModuleFE_AdvisorBoard.getPanelItem(PanelKey_Spotlight);
        if (panelItem && panelItem.height > 0)
            ModuleFE_Spotlight.closeSpotlight();
        //update selected item
        const currentSelected = ModuleFE_Inbox.getSelectedInboxItem();
        if (!currentSelected || currentSelected.itemId !== this.state.rtOrder._id) {
            //trigger findings panel
            ModuleFE_PanelFindings.jumpToFinding(this.state.rtOrder._id);
            ModuleFE_Inbox.setSelectedInboxItem({ type: 'inbox-order', itemId: this.state.rtOrder._id });
        }
        else
            ModuleFE_Inbox.setSelectedInboxItem();
        if (this.state.rtOrder.orderStatus === RuntimeOrderStatus_Enum.RESULTED)
            ModuleFE_EMInterface.vv1.orders.completeOrders({
                runtimeOrderIds: [this.state.rtOrder._id]
            }).executeSync();
    }
    //######################### Logic #########################
    getOrderCountForVariableId(rtOrder) {
        const dbOrder = ModuleFE_Advisor_Action.cache.unique(rtOrder.orderId);
        if (!ModuleFE_Advisor_Action.is.order(dbOrder))
            return 0;
        const prevRTOrders = ModuleFE_RuntimeOrder.cache.filter(i => i.__created < rtOrder.__created);
        const prevRTOrdersUnderSameVariable = prevRTOrders.filter(i => {
            const prevDbOrder = ModuleFE_Advisor_Action.cache.unique(i.orderId);
            return ModuleFE_Advisor_Action.is.order(prevDbOrder) && !!prevDbOrder.variableId && prevDbOrder.variableId === dbOrder.variableId;
        });
        return prevRTOrdersUnderSameVariable.length;
    }
    //######################### Logic - Order Resolving #########################
    resolveOrder = async () => {
        //Check if actual FHIR order was resolved and we didn't sync yet
        const currentRtOrderId = this.state.rtOrder._id;
        const emResponse = await ModuleFE_EMInterface.updateEncounterApis.refreshFhirData();
        //If order was resolved - update IDB
        if (this.checkIfOrderResulted(currentRtOrderId, emResponse.runtimeOrder?.updatedRuntimeOrders)) {
            return await this.handleResolvedOrderFromFhir(emResponse);
        }
        //Open wizard if the db order is connected to one
        if (this.state.dbOrder.wizardId)
            return this.openAsWizard();
        //Open manual resolving
        return this.handleManualResolving();
    };
    checkIfOrderResulted = (currentRtOrderId, rtOrders) => rtOrders?.some(order => (order._id === currentRtOrderId) && (order.orderStatus === RuntimeOrderStatus_Enum.RESULTED));
    handleResolvedOrderFromFhir = async (emResponse) => {
        // update idb
        await ModuleFE_EMInterface.handleEMResponse(emResponse);
        // select inbox item
        ModuleFE_Inbox.setSelectedInboxItem({ type: 'inbox-order', itemId: this.state.rtOrder._id });
        // jump to the finding
        ModuleFE_PanelFindings.jumpToFinding(this.state.rtOrder._id);
    };
    openAsWizard = () => {
        // call for snippet resolving if order has a related snippet
        const wizard = ModuleFE_Advisor_AdvisorWizard.cache.unique(this.state.dbOrder.wizardId); //fixme: old lib import
        if (!wizard)
            throw new MUSTNeverHappenException(`Couldn't find wizard for id ${this.state.dbOrder.wizardId}`);
        ModuleFE_Inbox.setSelectedInboxItem({ type: 'inbox-order', itemId: this.state.rtOrder._id });
        return ModuleFE_Spotlight.openWizard({
            wizardId: wizard._id,
            contextId: this.state.rtOrder._id,
            contextType: 'order',
        });
    };
    handleManualResolving = () => {
        return Advisor_DialogConfirmation.show({
            confirmCallback: () => {
                ModuleFE_Spotlight.openResolveOrder({ rtOrderId: this.state.rtOrder._id, dbOrderId: this.state.dbOrder._id });
                // only if manual resolution is selected mark the item
                ModuleFE_Inbox.setSelectedInboxItem({ type: 'inbox-order', itemId: this.state.rtOrder._id });
            },
            content: noFhirResults(this.state.dbOrder.name),
            confirmButtonLabel: 'Resolve manually'
        });
    };
}
const noFhirResults = (orderName) => _jsxs(_Fragment, { children: ["No results for ", _jsx("b", { children: orderName }), " were found in the EHR.", _jsx("br", {}), "Would you like proceed and resolve this order manually?"] });
//# sourceMappingURL=Component_InboxItem_Order.js.map